import { useQuery } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { formatMoney } from '@/format/money'
import {
  fetchBankAccounts,
  fetchContacts,
  fetchFinance,
  fetchTimeline,
  type AgeingLine,
  type BankAccount,
  type CardContact,
  type CardContract,
  type Finance,
  type Timeline,
} from './counterpartyApi'
import type { CardTab, CardTabContext } from './types'
import { Link } from 'react-router-dom'
import { NewContactForm } from './ContactTabs'

/**
 * Вкладки карточки контрагента (тикет #70, ТЗ §4.2).
 *
 * ## Почему это не общие вкладки каркаса
 *
 * Каркас #66 даёт четыре стандартные вкладки, три из которых заглушки (история, связанные,
 * вложения), и точку расширения `extraTabs`. Контакты, взаимодействия и финансы —
 * содержимое ровно этой сущности, и объявлять их в каркасе значило бы, что карточка сделки
 * начнёт знать про банковские счета.
 *
 * ## Что здесь общее для всех трёх вкладок
 *
 * **Загрузка, отказ и пустота — три разных состояния, и все три названы.** Пустой список
 * без надписи читается как «данных нет», а он с равным успехом означает «ещё грузим» и
 * «сервер отказал». Клиент, у которого «нет контактов», потому что запрос упал с 403,
 * получает звонок в поддержку, а не строку в логе.
 */

/** Три состояния запроса одним компонентом: иначе их путают в каждой вкладке заново. */
function AsyncSection<T>({
  query,
  empty,
  children,
}: {
  query: { isLoading: boolean; error: unknown; data: T | undefined }
  /** Что показать, когда данные пришли и их нет. Своя фраза на каждую вкладку. */
  empty: string
  children: (data: T) => React.ReactNode
}) {
  const { t } = useApp()

  if (query.isLoading) return <p>{t('app.loading')}</p>
  if (query.error) {
    return (
      <p role="alert">
        {query.error instanceof ApiError && query.error.isForbidden
          ? t('access.denied')
          : t('app.error')}
      </p>
    )
  }
  if (!query.data) return <p className="muted">{empty}</p>
  return <>{children(query.data)}</>
}

// ------------------------------------------------------------------ контакты

function ContactsPanel({ recordId }: { recordId: string }) {
  const { t } = useApp()
  const query = useQuery({
    queryKey: ['counterparty-contacts', recordId],
    queryFn: ({ signal }) => fetchContacts(recordId, signal),
    retry: false,
  })

  return (
    <AsyncSection query={query} empty={t('card.cp.contacts.empty')}>
      {(contacts: CardContact[]) =>
        contacts.length === 0 ? (
          <p className="muted">{t('card.cp.contacts.empty')}</p>
        ) : (
          <table className="card-table">
            <thead>
              <tr>
                <th>{t('card.cp.contact.name')}</th>
                <th>{t('card.cp.contact.role')}</th>
                <th>{t('column.phone')}</th>
                <th>{t('column.email')}</th>
                <th>{t('card.cp.contact.consent')}</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact.id} className={contact.primary ? 'card-row-primary' : undefined}>
                  <td>
                    {/* Ссылка на карточку контакта (#98): правка — там, одной транзакцией. */}
                    <Link to={`/contacts/${contact.id}`}>{contact.fullName}</Link>
                    {/* Основной помечается словом, а не только жирностью строки:
                        цветовой признак не переживает ни печать, ни дальтонизм. */}
                    {contact.primary && (
                      <span className="badge"> {t('card.cp.contact.primary')}</span>
                    )}
                  </td>
                  <td>{contact.jobTitle ?? '—'}</td>
                  <td>{contact.mobilePhone ?? contact.phone ?? '—'}</td>
                  <td>{contact.email ?? '—'}</td>
                  <td>
                    {/* Отсутствие согласия — предупреждение, а не пустая ячейка: звонить
                        человеку без согласия на обработку ПДн нельзя, и молчание об этом
                        перекладывает нарушение на того, кто просто открыл карточку. */}
                    {contact.consentGiven ? t('common.yes') : t('card.cp.contact.noConsent')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    </AsyncSection>
  )
}

// ------------------------------------------------------------------ реквизиты счетов

function BankAccountsPanel({ recordId }: { recordId: string }) {
  const { t } = useApp()
  const query = useQuery({
    queryKey: ['counterparty-bank-accounts', recordId],
    queryFn: ({ signal }) => fetchBankAccounts(recordId, signal),
    retry: false,
  })

  return (
    <AsyncSection query={query} empty={t('card.cp.accounts.empty')}>
      {(accounts: BankAccount[]) =>
        accounts.length === 0 ? (
          <p className="muted">{t('card.cp.accounts.empty')}</p>
        ) : (
          <table className="card-table">
            <thead>
              <tr>
                <th>{t('card.cp.account.bank')}</th>
                <th>{t('card.cp.account.bik')}</th>
                <th>{t('card.cp.account.number')}</th>
                <th>{t('column.currency')}</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.id}>
                  <td>
                    {account.bankName}
                    {account.primary && (
                      <span className="badge"> {t('card.cp.account.primary')}</span>
                    )}
                  </td>
                  <td>{account.bik}</td>
                  {/* Полный номер, а не маска: по маске платёж не сверить, а карточку
                      открывает тот, кому право на неё уже выдано. Маска нужна там, где
                      реквизиты показывают мимоходом, и это другой экран. */}
                  <td className="mono">{account.accountNumber}</td>
                  <td>{account.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    </AsyncSection>
  )
}

/**
 * Контакты и банковские реквизиты вместе.
 *
 * Одна вкладка, потому что и то и другое отвечает на «как с ними работать»: имя, кому
 * звонить, и счёт, куда платить. Разнести их значило бы две вкладки по одному списку.
 */
function ContactsAndAccounts({ recordId }: { recordId: string }) {
  const { t } = useApp()
  return (
    <div className="card-gap">
      <ContactsPanel recordId={recordId} />
      <NewContactForm counterpartyId={recordId} />
      <h3>{t('card.cp.accounts.title')}</h3>
      <BankAccountsPanel recordId={recordId} />
    </div>
  )
}

export function contactsTab(): CardTab {
  return {
    id: 'contacts',
    labelKey: 'card.tab.contacts',
    render: (context: CardTabContext) => <ContactsAndAccounts recordId={context.recordId} />,
  }
}

// ------------------------------------------------------------------ взаимодействия

function TimelinePanel({ recordId }: { recordId: string }) {
  const { t, locale } = useApp()
  const query = useQuery({
    queryKey: ['counterparty-timeline', recordId],
    queryFn: ({ signal }) => fetchTimeline(recordId, 50, signal),
    retry: false,
  })

  return (
    <AsyncSection query={query} empty={t('card.cp.timeline.empty')}>
      {(timeline: Timeline) => (
        <div className="card-gap">
          {/* Счётчик приходит вместе с лентой (#67) и показывается, даже когда лента
              пуста: «ни разу не касались» — это факт о клиенте, а не отсутствие данных. */}
          <p>{t('card.cp.timeline.count', { count: timeline.touchCount })}</p>
          {timeline.activities.length === 0 ? (
            <p className="muted">{t('card.cp.timeline.empty')}</p>
          ) : (
            <ul className="card-timeline">
              {timeline.activities.map((activity) => (
                <li key={activity.id} className={activity.overdue ? 'overdue' : undefined}>
                  <span className="timeline-when">
                    {formatMoment(activity.createdAt, locale)}
                  </span>
                  {/* Тип звонка/письма/задачи — переводится, а не показывается кодом:
                      `call` на экране читается как техническая деталь. Непереведённый
                      ключ вернётся сам собой и будет виден — это лучше пустоты. */}
                  <span className="timeline-kind">{t(`activity.type.${activity.type}`)}</span>
                  <span className="timeline-subject">{activity.subject ?? '—'}</span>
                  {activity.overdue && (
                    <span className="badge">{t('card.cp.timeline.overdue')}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </AsyncSection>
  )
}

/**
 * Единая лента звонков, писем, встреч и задач (ТЗ §4.2).
 *
 * Одна лента, а не четыре списка по типу: вопрос «что у нас с этим клиентом» не разбит по
 * каналам, и разбитый ответ заставляет читателя склеивать хронологию в голове.
 */
export function interactionsTab(): CardTab {
  return {
    id: 'interactions',
    labelKey: 'card.tab.interactions',
    render: (context: CardTabContext) => <TimelinePanel recordId={context.recordId} />,
  }
}

// ------------------------------------------------------------------ финансы

function AgeingTable({ lines }: { lines: AgeingLine[] }) {
  const { t, locale } = useApp()

  if (lines.length === 0) return <p className="muted">{t('card.cp.ageing.none')}</p>

  return (
    <table className="card-table">
      <thead>
        <tr>
          <th>{t('column.currency')}</th>
          <th>{t('card.cp.ageing.current')}</th>
          <th>{t('card.cp.ageing.d30')}</th>
          <th>{t('card.cp.ageing.d60')}</th>
          <th>{t('card.cp.ageing.d90')}</th>
          <th>{t('card.cp.ageing.d90plus')}</th>
          <th>{t('card.cp.ageing.total')}</th>
        </tr>
      </thead>
      <tbody>
        {/* Строка на валюту, и итог считается ВНУТРИ строки. Колонки «всего по всем
            валютам» здесь нет и быть не может: она потребовала бы курса, которого у
            системы нет, — см. `DunningService.ageingByCurrency`. */}
        {lines.map((line) => (
          <tr key={line.currency}>
            <td>{line.currency}</td>
            <td>{formatMoney(line.current, line.currency, locale)}</td>
            <td>{formatMoney(line.upTo30Days, line.currency, locale)}</td>
            <td>{formatMoney(line.upTo60Days, line.currency, locale)}</td>
            <td>{formatMoney(line.upTo90Days, line.currency, locale)}</td>
            <td>{formatMoney(line.beyond90Days, line.currency, locale)}</td>
            <td>
              {formatMoney(
                line.current +
                  line.upTo30Days +
                  line.upTo60Days +
                  line.upTo90Days +
                  line.beyond90Days,
                line.currency,
                locale,
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function ContractsTable({ contracts }: { contracts: CardContract[] }) {
  const { t, locale } = useApp()

  if (contracts.length === 0) return <p className="muted">{t('card.cp.contracts.empty')}</p>

  return (
    <table className="card-table">
      <thead>
        <tr>
          <th>{t('card.cp.contract.number')}</th>
          <th>{t('card.cp.contract.type')}</th>
          <th>{t('column.status')}</th>
          <th>{t('card.cp.contract.valid')}</th>
          <th>{t('column.amount')}</th>
        </tr>
      </thead>
      <tbody>
        {contracts.map((contract) => (
          <tr key={contract.id}>
            <td>{contract.number}</td>
            <td>{t(`contract.type.${contract.contractType}`)}</td>
            <td>
              {t(`contract.status.${contract.status}`)}
              {/* Статус и пригодность — разные вещи: договор, оставленный в `active`
                  после даты окончания, статусом выглядит рабочим. Считает это сервер
                  (`usableToday`), и показать надо именно расхождение — иначе заказ
                  оформят по договору, который уже не действует. */}
              {contract.status === 'active' && !contract.usableToday && (
                <span className="badge badge-warn"> {t('card.cp.contract.lapsed')}</span>
              )}
            </td>
            <td>
              {contract.validFrom ?? '—'} … {contract.validUntil ?? '—'}
            </td>
            <td>{formatMoney(contract.amount, contract.currency, locale)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/**
 * Кредитный лимит и предупреждение при превышении (ТЗ §4.2).
 *
 * Состояние приходит **с сервера** (`creditState`), а не считается здесь: превышение
 * определяет `CounterpartyService`, и он же откажет в заказе. Своя формула на клиенте
 * означала бы карточку, обещающую заказ, который не пройдёт, — или пугающую отказом,
 * которого не будет.
 */
export function CreditPanel({ finance }: { finance: Finance }) {
  const { t, locale } = useApp()

  return (
    <div className="card-gap">
      {finance.blocked && (
        <p className="notice notice-error" role="alert">
          {t('card.cp.blocked', { reason: finance.blockReason ?? '—' })}
        </p>
      )}

      {finance.creditState === 'exceeded' && (
        <p className="notice notice-error" role="alert">
          {t('card.cp.credit.exceeded', {
            debt: formatMoney(finance.outstandingDebt, null, locale),
            limit: formatMoney(finance.creditLimit ?? 0, null, locale),
          })}
        </p>
      )}

      <dl className="card-form">
        <div className="card-field">
          <dt>{t('column.credit_limit')}</dt>
          <dd>
            {/* «Лимита нет» и «лимит равен нулю» — противоположные утверждения: первое
                означает безграничный кредит доверенному клиенту, второе запрещает любой.
                Показать null как «0» значит поменять их местами. */}
            {finance.unlimitedCredit ? t('card.cp.credit.unlimited') : finance.creditLimit}
          </dd>
        </div>
        <div className="card-field">
          <dt>{t('card.cp.credit.debt')}</dt>
          <dd>{finance.outstandingDebt}</dd>
        </div>
        <div className="card-field">
          <dt>{t('card.cp.credit.available')}</dt>
          <dd>
            {finance.unlimitedCredit ? t('card.cp.credit.unlimited') : finance.availableCredit}
          </dd>
        </div>
        <div className="card-field">
          <dt>{t('card.cp.credit.terms')}</dt>
          <dd>{t('card.cp.credit.termsDays', { days: finance.paymentTermsDays })}</dd>
        </div>
      </dl>
    </div>
  )
}

function FinancePanel({ recordId }: { recordId: string }) {
  const { t, user } = useApp()

  // Право читается ЗДЕСЬ, а не передаётся снаружи, потому что `extraTabs` каркаса — это
  // статический массив в описании карточки (`descriptors.ts`), собранный на импорте
  // модуля. Вкладка, получившая права параметром, застыла бы на правах того, кто первым
  // открыл приложение, и после смены пользователя показывала бы чужой доступ.
  const canReadInvoices = can(user, 'billing.invoice', 'read')

  const query = useQuery({
    queryKey: ['counterparty-finance', recordId],
    // Запрос не отправляется без права на счета: он всё равно вернёт 403, и единственным
    // результатом был бы красный текст вместо объяснения. Запрет при этом остаётся на
    // сервере — скрытая вкладка это удобство, а не защита.
    enabled: canReadInvoices,
    queryFn: ({ signal }) => fetchFinance(recordId, signal),
    retry: false,
  })

  if (!canReadInvoices) {
    return <p className="muted">{t('card.cp.finance.needsBilling')}</p>
  }

  return (
    <AsyncSection query={query} empty={t('card.cp.finance.empty')}>
      {(finance: Finance) => (
        <div className="card-gap">
          <CreditPanel finance={finance} />

          <h3>{t('card.cp.ageing.title')}</h3>
          <AgeingTable lines={finance.ageingByCurrency} />

          <h3>{t('card.cp.contracts.title')}</h3>
          <ContractsTable contracts={finance.contracts} />
        </div>
      )}
    </AsyncSection>
  )
}

/**
 * Вкладка «Финансы»: лимит, долг, ageing, договоры.
 *
 * Требует **двух** прав, и второе проверяется внутри панели только чтобы объяснить
 * пустоту: настоящий запрет — `@PreAuthorize` на эндпоинте. Прочитать отсутствие права
 * как «ограничений нет» — классический способ показать чужой раздел, поэтому по умолчанию
 * данные не запрашиваются.
 *
 * <p>Вкладка при этом **видна** и без права: она объясняет, чего не хватает. Спрятать её
 * целиком было бы честнее, если бы речь шла о разделе меню, но здесь исчезнувшая вкладка
 * читается как «у этого клиента нет финансов», а это неправда о клиенте.
 */
export function financeTab(): CardTab {
  return {
    id: 'finance',
    labelKey: 'card.tab.finance',
    render: (context: CardTabContext) => <FinancePanel recordId={context.recordId} />,
  }
}

/**
 * Все вкладки контрагента.
 *
 * Статический массив: права читает сама вкладка через `useApp`, поэтому собрать их можно
 * на импорте модуля — ровно то, чего ждёт `extraTabs` каркаса #66.
 */
export function counterpartyTabs(): CardTab[] {
  return [contactsTab(), interactionsTab(), financeTab()]
}

/**
 * Момент времени для ленты.
 *
 * Дата и время вместе: «звонили 3 марта» и «звонили 3 марта в 18:40» отвечают на разные
 * вопросы, и второй задают, когда выясняют, кто с кем говорил последним.
 */
function formatMoment(value: string, locale: string): string {
  const moment = new Date(value)
  if (Number.isNaN(moment.getTime())) return value
  return moment.toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU')
}
